import { Injectable } from '@angular/core';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { NotificationService } from '../../services/notification.service';
import { Observable } from 'rxjs';
import { withLatestFrom } from 'rxjs/operators';

import {
  CompanyUsers,
  CompanyUsersSuccess,
  CurrentUserLoadSuccess,
  ProjectUsers,
  ProjectUsersSuccess,
  SaveCalendarConsent,
  SaveCalendarConsentFailure,
  SaveCalendarConsentSuccess,
  UserActionTypes,
} from '@completion/actions';
import { UserPreference } from '@completion/enums';
import { State } from '@completion/reducers';
import { getCurrentProject } from '@completion/selectors';
import { UserService } from '../../services';
import { ResourceEffect } from './resource.effect';
import { Project, User } from '@completion/models';
import { trackResource } from './resource-fetch.util';

@Injectable()
export class UserEffects extends ResourceEffect {
  constructor(
    readonly actions$: Actions,
    readonly service: UserService,
    readonly store: Store<State>,
    readonly toastr: NotificationService,
  ) {
    super(actions$, service, store, toastr);
  }

  loadCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActionTypes.CurrentUserLoad),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'getCurrentUser',
        serviceArgs: () => [],
        onSuccess: (user) => new CurrentUserLoadSuccess(user as any),
      }),
    ),
  );

  loadProjectUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType<ProjectUsers>(UserActionTypes.ProjectUsers),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'getUsers',
        serviceArgs: ([_, currentProject]) => [currentProject.id],
        onSuccess: (users) => new ProjectUsersSuccess(users as any),
      }),
    ),
  );

  loadCompanyUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CompanyUsers>(UserActionTypes.CompanyUsers),
      withLatestFrom(this.store.select(getCurrentProject) as Observable<Project>),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'getUsers',
        serviceArgs: ([action, currentProject]) => [currentProject.id, action.companyId],
        onSuccess: (users) => new CompanyUsersSuccess(users as any),
      }),
    ),
  );

  saveCalendarConsent$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SaveCalendarConsent>(UserActionTypes.SaveCalendarConsent),
      trackResource({
        actions$: this.actions$,
        store: this.store,
        toastr: this.toastr,
        service: this.service,
        method: 'saveUserPreference',
        serviceArgs: (action) => [UserPreference.CONSENT_TO_CALENDAR_INVITES, (action as SaveCalendarConsent).consent],
        onSuccess: (user) => new SaveCalendarConsentSuccess(user as User),
        onError: (error) => new SaveCalendarConsentFailure(error),
      }),
    ),
  );
}
